import React, { useState, useRef, useEffect } from 'react';
import { GripVertical, X, Upload, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../services/firebase';
import { clsx } from 'clsx';

const getDefaultPosition = (anchorRect) => {
  if (anchorRect) {
    const x = Math.max(80, Math.min(window.innerWidth - 460, anchorRect.left + 40));
    const y = Math.max(80, Math.min(window.innerHeight - 420, anchorRect.top + 40));
    return { x, y };
  }
  return {
    x: Math.max(80, window.innerWidth / 2 - 220),
    y: Math.max(80, window.innerHeight / 2 - 200),
  };
};

const ImageUploadModal = ({ isOpen, userId, anchorRect, onInsert, onClose }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [align, setAlign] = useState('left');
  const [editorPosition, setEditorPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [dragOver, setDragOver] = useState(false);
  const modalRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setImageUrl('');
    setUploading(false);
    setUploadError('');
    setAlign('left');
    setEditorPosition(getDefaultPosition(anchorRect));
  }, [isOpen, anchorRect]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Drag-to-move modal
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const cx = e.clientX || (e.touches && e.touches[0]?.clientX);
      const cy = e.clientY || (e.touches && e.touches[0]?.clientY);
      if (cx && cy) {
        const w = modalRef.current?.offsetWidth || 440;
        const h = modalRef.current?.offsetHeight || 400;
        setEditorPosition({
          x: Math.max(0, Math.min(cx - dragStartPos.x, window.innerWidth - w)),
          y: Math.max(0, Math.min(cy - dragStartPos.y, window.innerHeight - h)),
        });
      }
    };
    const onUp = () => setIsDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };
  }, [isDragging, dragStartPos]);

  const startDrag = (e) => {
    setIsDragging(true);
    setDragStartPos({
      x: (e.clientX || e.touches?.[0]?.clientX || 0) - editorPosition.x,
      y: (e.clientY || e.touches?.[0]?.clientY || 0) - editorPosition.y,
    });
    e.preventDefault();
  };

  const uploadFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setUploadError('Please select a valid image file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Image must be under 10 MB.');
      return;
    }

    setUploading(true);
    setUploadError('');
    try {
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `sigma-images/${userId}/${timestamp}_${safeName}`;
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      setImageUrl(url);
    } catch (err) {
      console.error('Image upload failed:', err);
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDropFile = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleInsert = () => {
    if (!imageUrl) return;
    onInsert?.({ url: imageUrl, align });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 overflow-hidden">
      <div
        ref={modalRef}
        className="absolute bg-white rounded-lg shadow-lg"
        style={{
          left: `${editorPosition.x}px`,
          top: `${editorPosition.y}px`,
          width: '440px',
          maxWidth: '95vw',
        }}
      >
        {/* Title bar */}
        <div className="flex items-center justify-between bg-gray-100 px-4 py-2 rounded-t-lg">
          <div
            onMouseDown={startDrag}
            onTouchStart={startDrag}
            className="cursor-move bg-gray-200 hover:bg-gray-300 p-2 rounded mr-2 flex items-center justify-center"
            title="Drag to move"
          >
            <GripVertical size={16} className="mr-1" />
            <span className="text-xs">Drag</span>
          </div>
          <h3 className="ml-2 text-sm md:text-lg font-medium flex-grow">Insert Image</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-2" title="Close">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Drop zone / file picker */}
          {!imageUrl && (
            <div
              className={clsx(
                'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
                dragOver ? 'border-sigma-500 bg-sigma-50' : 'border-gray-300 hover:border-gray-400'
              )}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDropFile}
            >
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                {uploading ? 'Uploading...' : 'Click or drop an image here'}
              </p>
              {uploading && (
                <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-sigma-600 rounded-full animate-pulse" style={{ width: '60%' }} />
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          )}

          {/* Preview */}
          {imageUrl && (
            <div className="relative">
              <img
                src={imageUrl}
                alt="Preview"
                className="max-h-48 mx-auto rounded border border-gray-200"
              />
              <button
                onClick={() => { setImageUrl(''); setUploadError(''); }}
                className="absolute top-1 right-1 w-6 h-6 bg-white rounded-full shadow flex items-center justify-center text-gray-500 hover:text-red-500"
                title="Remove"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}

          {/* Alignment */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Alignment</label>
            <div className="flex gap-1">
              {[
                { value: 'left', icon: AlignLeft, label: 'Float Left' },
                { value: 'center', icon: AlignCenter, label: 'Center' },
                { value: 'right', icon: AlignRight, label: 'Float Right' },
              ].map(({ value, icon: Icon, label }) => (
                <button
                  key={value}
                  onClick={() => setAlign(value)}
                  title={label}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm transition-colors',
                    align === value
                      ? 'bg-sigma-700 text-white border-sigma-700'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  )}
                >
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className={clsx(
                'px-4 py-2 rounded-md text-sm text-white',
                imageUrl ? 'bg-sigma-700 hover:bg-sigma-600' : 'bg-gray-300 cursor-not-allowed'
              )}
              disabled={!imageUrl}
              onClick={handleInsert}
            >
              Insert
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadModal;
